import type React from "react";
import { CheckCircle2, Circle, Clock } from "lucide-react";
import type { ChipTone } from "@/components/ui/chips";
import type { StatusBadgeVariant } from "@/components/ui/status-badge";
import type { RequestStatus } from "@/hooks/useRequests";
import type { ProjectStatus } from "@/hooks/useProjects";

export type StatusTone = ChipTone;

export const TASK_STATUS = {
  todo: "todo",
  in_progress: "in_progress",
  done: "done",
} as const;

export type CanonicalTaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];
export type LegacyTaskStatus = "open" | "completed" | "cancelled";
export type TaskStatusValue = CanonicalTaskStatus | LegacyTaskStatus;

export const TASK_STATUS_ALIAS_MAP: Record<LegacyTaskStatus, CanonicalTaskStatus> = {
  open: TASK_STATUS.todo,
  completed: TASK_STATUS.done,
  cancelled: TASK_STATUS.done,
};

export const TASK_STATUS_ORDER: CanonicalTaskStatus[] = [
  TASK_STATUS.todo,
  TASK_STATUS.in_progress,
  TASK_STATUS.done,
];
export const toCanonicalTaskStatus = (status: TaskStatusValue): CanonicalTaskStatus => {
  const canonicalStatus = TASK_STATUS_ALIAS_MAP[status as LegacyTaskStatus];
  if (canonicalStatus) {
    return canonicalStatus;
  }
  return status as CanonicalTaskStatus;
};

export const TASK_STATUS_CONFIG = {
  todo: { label: "To Do", tone: "neutral", variant: "neutral", icon: Circle },
  in_progress: { label: "In Progress", tone: "info", variant: "info", icon: Clock },
  done: { label: "Done", tone: "success", variant: "success", icon: CheckCircle2 },
} satisfies Record<
  CanonicalTaskStatus,
  { label: string; tone: StatusTone; variant: StatusBadgeVariant; icon: React.ElementType }
>;

export const getTaskStatusLabel = (status?: TaskStatusValue | null): string => {
  if (!status) return "Unknown";
  const canonicalStatus = toCanonicalTaskStatus(status);
  const config = TASK_STATUS_CONFIG[canonicalStatus];
  if (config) return config.label;
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const REQUEST_STATUS_ORDER: RequestStatus[] = [
  "open",
  "in_progress",
  "waiting",
  "resolved",
  "closed",
];

export const REQUEST_STATUS_CONFIG = {
  open: { label: "Open", tone: "info" },
  in_progress: { label: "In Progress", tone: "warning" },
  waiting: { label: "Waiting", tone: "neutral" },
  resolved: { label: "Resolved", tone: "success" },
  closed: { label: "Closed", tone: "neutral" },
} satisfies Record<RequestStatus, { label: string; tone: StatusTone }>;

export const PROJECT_STATUS_CONFIG = {
  planning: { label: "Planning", tone: "neutral" },
  active: { label: "Active", tone: "info" },
  on_hold: { label: "On Hold", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  cancelled: { label: "Cancelled", tone: "neutral" },
} satisfies Record<ProjectStatus, { label: string; tone: StatusTone }>;

export type { FinanceQueueStatus } from "@/api/finance-queue";

export const FINANCE_QUEUE_STATUS_CONFIG: Record<
  import("@/api/finance-queue").FinanceQueueStatus,
  { label: string; tone: StatusTone }
> = {
  needs_review: { label: "Needs Review", tone: "warning" },
  needs_allocation: { label: "Needs Allocation", tone: "warning" },
  sync_failed: { label: "Sync Failed", tone: "error" },
  sync_pending: { label: "Sync Pending", tone: "info" },
  reviewed: { label: "Reviewed", tone: "success" },
  ready_for_export: { label: "Ready for Export", tone: "success" },
};
